import type { FileStatus } from '@jmc/shared';
import {
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useId,
  useRef,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';
import { useI18n } from '../lib/i18n.js';

export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

// ---------------------------------------------------------------------------
// Status
// ---------------------------------------------------------------------------

const STATUS_STYLE: Record<FileStatus, string> = {
  AVAILABLE: 'bg-green-bg text-green-ink border-green-line',
  ISSUED: 'bg-navy-50 text-navy-700 border-navy-100',
  MISSING: 'bg-red-bg text-red-ink border-red-line',
  WEEDED: 'bg-paper text-ink-500 border-line-strong',
};

export function StatusBadge({
  status,
  overdue = false,
}: {
  status: FileStatus;
  overdue?: boolean;
}) {
  const { t } = useI18n();
  if (overdue && status === 'ISSUED') {
    return (
      <span className="badge bg-amber-bg text-amber-ink border-amber-line">
        <Dot className="bg-amber-ink" />
        {t('status.overdue')}
      </span>
    );
  }
  return (
    <span className={cx('badge', STATUS_STYLE[status])}>
      <Dot
        className={cx(
          status === 'AVAILABLE' && 'bg-green-ink',
          status === 'ISSUED' && 'bg-navy-600',
          status === 'MISSING' && 'bg-red-ink',
          status === 'WEEDED' && 'bg-ink-400',
        )}
      />
      {t(`status.${status}`)}
    </span>
  );
}

function Dot({ className }: { className?: string }) {
  return <span className={cx('h-1.5 w-1.5 rounded-full', className)} aria-hidden="true" />;
}

// ---------------------------------------------------------------------------
// Layout primitives
// ---------------------------------------------------------------------------

export function Panel({
  title,
  action,
  children,
  className,
  bodyClassName,
}: {
  title?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  return (
    <section className={cx('panel', className)}>
      {(title || action) && (
        <header className="panel-header">
          <h2 className="text-[0.9375rem] font-semibold">{title}</h2>
          {action}
        </header>
      )}
      <div className={bodyClassName}>{children}</div>
    </section>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
      <div className="min-w-0">
        <h1 className="text-xl font-semibold">{title}</h1>
        {/* A div, not a p: callers pass breadcrumb <nav> and badge rows in
            here, and block elements inside a <p> are invalid nesting that
            React warns about and browsers silently restructure. */}
        {subtitle && <div className="mt-1 text-ink-500">{subtitle}</div>}
      </div>
      {actions && <div className="flex shrink-0 flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Loading, empty and error states
// ---------------------------------------------------------------------------

export function Spinner({ className }: { className?: string }) {
  return (
    <svg
      className={cx('animate-spin', className)}
      viewBox="0 0 16 16"
      width="16"
      height="16"
      aria-hidden="true"
    >
      <circle cx="8" cy="8" r="6.5" fill="none" stroke="currentColor" strokeOpacity="0.25" strokeWidth="2" />
      <path d="M14.5 8A6.5 6.5 0 0 0 8 1.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

export function LoadingBlock({ label }: { label?: string }) {
  const { t } = useI18n();
  return (
    <div className="flex items-center justify-center gap-2.5 py-14 text-ink-500">
      <Spinner />
      <span>{label ?? t('common.loading')}</span>
    </div>
  );
}

/** Skeleton rows, so a slow table does not collapse and jump. */
export function TableSkeleton({ rows = 6, columns = 5 }: { rows?: number; columns?: number }) {
  return (
    <div className="px-4 py-2" aria-hidden="true">
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="flex gap-4 border-b border-line py-3.5 last:border-0">
          {Array.from({ length: columns }).map((__, columnIndex) => (
            <div
              key={columnIndex}
              className="h-3.5 animate-pulse rounded bg-line"
              style={{ width: `${[22, 26, 16, 18, 14][columnIndex % 5]}%` }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export function EmptyState({
  title,
  hint,
  action,
}: {
  title: string;
  hint?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-16 text-center">
      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" aria-hidden="true" className="text-ink-400">
        <path
          d="M4 7.5A1.5 1.5 0 0 1 5.5 6h4l2 2.5h7A1.5 1.5 0 0 1 20 10v7.5a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 17.5v-10Z"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinejoin="round"
        />
      </svg>
      <p className="font-medium text-ink-700">{title}</p>
      {hint && <p className="max-w-sm text-ink-500">{hint}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const { t } = useI18n();
  const message = error instanceof Error ? error.message : String(error);
  return (
    <div
      role="alert"
      className="flex flex-col items-start gap-3 rounded-md border border-red-line bg-red-bg px-4 py-3.5"
    >
      <div>
        <p className="font-medium text-red-ink">{t('common.error')}</p>
        <p className="mt-0.5 text-ink-700">{message}</p>
      </div>
      {onRetry && (
        <button type="button" className="btn-secondary" onClick={onRetry}>
          {t('action.retry')}
        </button>
      )}
    </div>
  );
}

export function InlineError({ message }: { message: string }) {
  return (
    <p
      role="alert"
      className="rounded-[5px] border border-red-line bg-red-bg px-3 py-2 text-red-ink"
    >
      {message}
    </p>
  );
}

// ---------------------------------------------------------------------------
// Form fields
// ---------------------------------------------------------------------------

interface FieldProps {
  label: string;
  error?: string | undefined;
  hint?: string;
  required?: boolean;
  children: ReactNode;
  htmlFor?: string;
}

/**
 * Carries the generated id and error state from `Field` down to whichever
 * input it wraps.
 *
 * Without this every call site has to invent an id and remember to pass it to
 * both the label and the input; miss it once and a screen reader announces an
 * unlabelled box. The context makes the correct behaviour the default.
 */
const FieldContext = createContext<{ id: string; invalid: boolean; describedBy?: string } | null>(
  null,
);

export function Field({ label, error, hint, required, children, htmlFor }: FieldProps) {
  const generatedId = useId();
  const id = htmlFor ?? generatedId;
  const messageId = error || hint ? `${id}-message` : undefined;

  return (
    <FieldContext.Provider value={{ id, invalid: Boolean(error), describedBy: messageId }}>
      <div>
        <label className="field-label" htmlFor={id}>
          {label}
          {/* Marked by the asterisk and by `required` on the control, not by colour alone. */}
          {required && (
            <span className="ml-0.5 text-red-ink" aria-hidden="true">
              *
            </span>
          )}
        </label>
        {children}
        {hint && !error && (
          <p id={messageId} className="mt-1.5 text-[0.8125rem] text-ink-500">
            {hint}
          </p>
        )}
        {error && (
          <p id={messageId} role="alert" className="field-error">
            {error}
          </p>
        )}
      </div>
    </FieldContext.Provider>
  );
}

export const TextInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function TextInput({ className, id, ...props }, ref) {
    const field = useContext(FieldContext);
    return (
      <input
        ref={ref}
        id={id ?? field?.id}
        aria-invalid={field?.invalid || undefined}
        aria-describedby={field?.describedBy}
        className={cx('field-input', className)}
        {...props}
      />
    );
  },
);

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className, children, id, ...props }, ref) {
    const field = useContext(FieldContext);
    return (
      <select
        ref={ref}
        id={id ?? field?.id}
        aria-invalid={field?.invalid || undefined}
        aria-describedby={field?.describedBy}
        className={cx('field-input appearance-none pr-8', className)}
        {...props}
      >
        {children}
      </select>
    );
  },
);

// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------

export function Modal({
  open,
  title,
  onClose,
  children,
  footer,
  width = 'max-w-lg',
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  width?: string;
}) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);

    // Move focus into the dialog so keyboard users are not left behind it.
    const previous = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = overflow;
      previous?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink-900/35 px-4 py-10">
      <button
        type="button"
        aria-label="Close"
        tabIndex={-1}
        className="absolute inset-0 cursor-default"
        onClick={onClose}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={cx(
          'relative w-full rounded-lg border border-line bg-white shadow-xl outline-none',
          width,
        )}
      >
        <header className="flex items-center justify-between gap-4 border-b border-line px-5 py-3.5">
          <h2 className="text-[0.9375rem] font-semibold">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="btn-ghost -mr-2 px-2 py-1"
            aria-label="Close"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="m4 4 8 8m0-8-8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
          </button>
        </header>
        <div className="px-5 py-4">{children}</div>
        {footer && (
          <footer className="flex justify-end gap-2 border-t border-line bg-paper px-5 py-3.5">
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Misc
// ---------------------------------------------------------------------------

/** Key-value row used on detail screens. */
export function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[minmax(7.5rem,11rem)_1fr] gap-3 border-b border-line px-4 py-2.5 last:border-0">
      <dt className="text-ink-500">{label}</dt>
      <dd className="min-w-0 break-words text-ink-900">{children}</dd>
    </div>
  );
}

export function Mono({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span className={cx('font-mono text-[0.8125rem] tracking-tight', className)}>{children}</span>
  );
}

/**
 * Confirmation for an action that cannot be undone.
 *
 * Deactivating an account or resetting a password signs someone out everywhere
 * and cannot be reversed by clicking again, so neither happens on a single
 * stray click.
 */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel,
  tone = 'danger',
  busy = false,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  tone?: 'danger' | 'primary';
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { t } = useI18n();
  return (
    <Modal
      open={open}
      title={title}
      onClose={onCancel}
      width="max-w-md"
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onCancel} disabled={busy}>
            {t('action.cancel')}
          </button>
          <button
            type="button"
            className={tone === 'danger' ? 'btn-danger' : 'btn-primary'}
            onClick={onConfirm}
            disabled={busy}
            autoFocus
          >
            {busy && <Spinner />}
            {confirmLabel}
          </button>
        </>
      }
    >
      <p>{message}</p>
    </Modal>
  );
}

export function Toast({
  message,
  tone = 'success',
  onDismiss,
}: {
  message: string;
  tone?: 'success' | 'error';
  onDismiss: () => void;
}) {
  useEffect(() => {
    const timer = setTimeout(onDismiss, 6000);
    return () => clearTimeout(timer);
  }, [onDismiss]);

  return (
    <div
      role="status"
      className={cx(
        'fixed bottom-5 left-1/2 z-50 -translate-x-1/2 rounded-md border px-4 py-2.5 shadow-lg',
        tone === 'success'
          ? 'border-green-line bg-green-bg text-green-ink'
          : 'border-red-line bg-red-bg text-red-ink',
      )}
    >
      {message}
    </div>
  );
}
